import { useEffect, useRef } from 'react';

/**
 * 2px glow line showing page scroll progress, fixed right under the header
 * (follows the header's bottom edge when it hides on phones; falls back to var(--header-h)).
 */
export default function ScrollProgress({ className = '' }: { className?: string }) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const bar = ref.current;
    if (!bar) return;
    let frame = 0;
    let settleUntil = 0;
    let nav: HTMLElement | null = null;

    const update = () => {
      frame = 0;
      const doc = document.documentElement;
      const max = doc.scrollHeight - window.innerHeight;
      const progress = max > 0 ? Math.min(1, Math.max(0, window.scrollY / max)) : 0;
      if (!nav?.isConnected) nav = document.querySelector<HTMLElement>('.site-nav');
      const top = nav ? Math.max(0, nav.getBoundingClientRect().bottom) : null;
      bar.style.transform = `translate3d(0, ${top === null ? 'var(--header-h)' : `${top.toFixed(1)}px`}, 0) scaleX(${progress.toFixed(4)})`;
      // keep following the header while its show/hide transition finishes
      if (performance.now() < settleUntil) frame = requestAnimationFrame(update);
    };
    const schedule = () => {
      settleUntil = performance.now() + 650;
      if (!frame) frame = requestAnimationFrame(update);
    };

    schedule();
    window.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', schedule, { passive: true });
    return () => {
      window.removeEventListener('scroll', schedule);
      window.removeEventListener('resize', schedule);
      if (frame) cancelAnimationFrame(frame);
    };
  }, []);

  return <div ref={ref} className={`scroll-progress ${className}`} aria-hidden="true" />;
}
